import { Button, Input, Space, Tooltip } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { formatterPhone, formatCurrency } from "@zhihaoscm/utils";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import type { Page } from "@zhihaoscm/entity";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { useUserStore } from "@/views/stores";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { PROD_TAB } from "@/views/pages/tradeManage/goodsManagement/components";
import CaclenderTab from "@/views/pages/tradeManage/components/caclenderTab";
import FeeTab from "@/views/pages/tradeManage/components/feeTab";
import InvoiceInfo from "@/views/pages/tradeManage/components/invoiceInfo";
import DeliveryTable from "@/views/pages/tradeManage/goodsManagement/components/delivery/deliveryTable";
import OrderInfo from "@/views/pages/tradeManage/goodsManagement/consignment/components/orderInfo";
import { ProductDetailBankTable, ProductDetailMedia, ProductDetailRichText } from "./ProductDetailPrimitives";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { ConsignmentRow } from "./types";

export interface ConsignmentDetailPageProps {
    assignConsignment?: (id: number | string, handlerId: number) => Promise<void>;
    auditConsignment?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeConsignment?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfConsignment?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (
        keyword: string
    ) => Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>>;
    loadConsignment?: (id: number | string) => Promise<ConsignmentRow>;
    loadOrderTotal?: (id: number | string) => Promise<number>;
    permissions?: string[];
}

/** 复用 Vue 寄售商品详情接口。 */
export async function loadConsignmentDetail(id: number | string): Promise<ConsignmentRow> {
    const response = await request.service.get<ConsignmentRow>(`/admin/product-consignment/vo/${id}`);
    return response.data;
}

/** 复用旧 Vue 详情页的订单计数条件，决定是否展示明细 Tab。 */
export async function loadConsignmentOrderTotal(id: number | string): Promise<number> {
    const response = await request.service.get<Page<unknown>>("/admin/order/paging", {
        params: { productId: id, type: 1 }
    });
    return Number(response.data.total || 0);
}

/** 复用 Vue 寄售商品审核接口。 */
export async function auditConsignment(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-consignment/audit/${id}`, payload);
}

/** 复用 Vue 寄售商品下架接口。 */
export async function downShelfConsignment(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-consignment/down-shelf/${id}`);
}

/** 复用 Vue 寄售商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeConsignment(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-consignment/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 寄售商品指派接口。 */
export async function assignConsignment(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-consignment/assign/${id}/${handlerId}`);
}

/** 复用 Vue 寄售专员候选查询接口。 */
export async function findConsignmentSpecials(
    keyword: string
): Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>> {
    const response = await request.service.get<
        Array<{ user: { personId?: number; name?: string; employeeId?: string } }>
    >(`/admin/product-consignment/find/specials?name=${encodeURIComponent(keyword)}`);
    return response.data;
}

/** 将 Vue 寄售详情的交易枚举统一为 React 详情分支使用的数字。 */
function normalizeConsignmentDetail(row: ConsignmentRow): ConsignmentRow {
    return {
        ...row,
        productConsignment: {
            ...row.productConsignment,
            priceType: row.productConsignment.priceType == null ? undefined : Number(row.productConsignment.priceType),
            isIncludeTax:
                row.productConsignment.isIncludeTax == null ? undefined : Number(row.productConsignment.isIncludeTax),
            taxRate: row.productConsignment.taxRate == null ? undefined : Number(row.productConsignment.taxRate),
            waitingPayTime:
                row.productConsignment.waitingPayTime == null
                    ? undefined
                    : Number(row.productConsignment.waitingPayTime),
            state: row.productConsignment.state == null ? undefined : Number(row.productConsignment.state),
            publishState:
                row.productConsignment.publishState == null ? undefined : Number(row.productConsignment.publishState)
        }
    };
}

const paymentTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };
const taxRateLabels: Record<number, string> = { 1: "3%", 2: "6%", 3: "9%", 4: "13%" };
const priceTypeTips: Record<number, string> = {
    1: "固定价格：为实际交易价格，不受市场行情影响",
    2: "预估价格：价格可能存在变动，以提货当日价格为准"
};
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

const consignmentDetailTabs = [
    PROD_TAB.OVERVIEW,
    PROD_TAB.SCHEDULE,
    PROD_TAB.DELIVERY,
    PROD_TAB.ORDER,
    PROD_TAB.FEE,
    PROD_TAB.INVOICE,
    PROD_TAB.OPERATION_LOG
] as const;

/** 从详情地址恢复上次选中的页签，浏览器返回时继续展示离开前的业务区域。 */
function getConsignmentDetailTab(search: string): PROD_TAB {
    const tab = Number(new URLSearchParams(search).get("tab"));
    return consignmentDetailTabs.includes(tab as (typeof consignmentDetailTabs)[number])
        ? (tab as PROD_TAB)
        : PROD_TAB.OVERVIEW;
}

/** 将旧 Vue 数值字段保持为带单位的详情文本。 */
function formatWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "--" : `${formatCurrency(value)}${unit}`;
}

/** 旧 Vue fmtMoney 固定保留两位小数，避免销售单价的文本宽度发生变化。 */
function formatPrice(value: number | undefined): string {
    return value == null ? "--" : `${Number(value).toFixed(2)}元/吨`;
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    const formatDate = (value?: string) => value?.slice(0, 10) || "";
    if (start && end) return `${formatDate(start)} 至 ${formatDate(end)}`;
    return formatDate(start) || formatDate(end) || "--";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: ConsignmentRow["productConsignment"]["parameterInfo"]) {
    if (!parameterInfo?.length) return "--";
    /** 旧 Vue 通过 v-html 输出文本节点和 inline-flex 分隔块，保留同一 DOM 形态以避免参数逐项累积像素误差。 */
    return (
        <div className="html-value product-detail-parameter-value">
            {parameterInfo.map((item, index) => (
                <Fragment key={`${item.parameterName || "parameter"}-${index}`}>
                    {`${item.parameterName || "--"}  ${item.parameterValue || "--"}`}
                    {index < parameterInfo.length - 1 ? <div className="con-width product-detail-con-width" /> : null}
                </Fragment>
            ))}
        </div>
    );
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "--";
}

/** 以视觉分组展示手机号，同时保留无空格文本节点供详情契约和复制使用。 */
function renderHandlerMobiles(mobiles?: string[]) {
    if (!mobiles?.length) return "--";
    return mobiles.map((mobile, index) => {
        const groups = formatterPhone(mobile).split(" ");
        return (
            <span key={`${mobile}-${index}`} className="product-detail-phone">
                {groups.map((group, groupIndex) => (
                    <span key={`${group}-${groupIndex}`}>{group}</span>
                ))}
                {index < mobiles.length - 1 ? "; " : null}
            </span>
        );
    });
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: ConsignmentRow["productType"]): ReactNode {
    if (!productType?.name) return "--";
    /** 旧 Vue 品类值通过 html-value 和 split 节点输出，保留分隔符颜色及两侧间距。 */
    return (
        <div className="html-value product-detail-type-value">
            {productType.name}
            {productType.areaAbbreviation ? <span className="split"> ({productType.areaAbbreviation})</span> : null}
        </div>
    );
}

/** 兼容旧 Vue 企业对象的多个名称字段，避免详情接口字段变体显示为横杠。 */
function formatSupplierName(supplier?: ConsignmentRow["productConsignment"]["supplierEnterprise"]): string {
    return (
        supplier?.enterpriseName ||
        supplier?.supplierName ||
        supplier?.name ||
        supplier?.realName ||
        supplier?.mobile ||
        "--"
    );
}

/** 还原 Vue 详情页的含税文案，税率使用接口枚举对应的百分比。 */
function formatTaxValue(isIncludeTax?: number, taxRate?: number): string {
    if (isIncludeTax === 1) return `含税(${taxRateLabels[taxRate || 0] || "--"})`;
    if (isIncludeTax === 0) return "不含税";
    return "--";
}

/** 还原 Vue 详情页的定金方式及货款比例/固定金额。 */
function formatSettlementValue(product: ConsignmentRow["productConsignment"]): string {
    if (product.settlementMethod === 1) return "全部货款";
    if (product.settlementMethod !== 2) return "--";
    const method = product.depositPaymentMethod === 1 ? "货款比例" : "固定金额";
    const value =
        product.depositPaymentMethod === 1 ? `${product.paymentRatio ?? "--"}%` : `${product.fixedAmount ?? "--"}元`;
    return `支付定金: ${method}的${value}`;
}

/** 寄售商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function ConsignmentDetailPage({
    assignConsignment: assign = assignConsignment,
    auditConsignment: submitAudit = auditConsignment,
    closeConsignment: close = closeConsignment,
    downShelfConsignment: removeFromShelf = downShelfConsignment,
    findServiceSpecials: findSpecials = findConsignmentSpecials,
    loadConsignment = loadConsignmentDetail,
    loadOrderTotal = loadConsignmentOrderTotal,
    permissions
}: ConsignmentDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    // 商品详情可从运营分析进入；优先返回来源页，保留 Vue 页面的上下文路径。
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<ConsignmentRow>();
    const [product, setProduct] = useState<ConsignmentRow["productConsignment"]>();
    const [orderTotal, setOrderTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);
    const [operationLogRefreshToken, setOperationLogRefreshToken] = useState(0);
    const [assignOpen, setAssignOpen] = useState(false);
    const [specialists, setSpecialists] = useState<Awaited<ReturnType<typeof findConsignmentSpecials>>>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${isAuditPage ? "审核寄售商品" : "寄售商品详情"} - 链云砂石`;
        return () => {
            document.title = previousTitle;
        };
    }, [isAuditPage]);

    useEffect(() => {
        if (!id) return;
        void loadConsignment(id)
            .then(result => {
                const normalizedResult = normalizeConsignmentDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productConsignment);
            })
            .catch(() => setErrorMessage("寄售商品详情加载失败"));
    }, [id, loadConsignment]);

    useEffect(() => {
        let disposed = false;
        if (!id) {
            setOrderTotal(0);
            return;
        }
        // 旧 Vue 仅在商品已上架或存在订单时显示明细页签，订单计数失败不应阻断详情页。
        void loadOrderTotal(id)
            .then(total => {
                if (!disposed) setOrderTotal(Number.isFinite(total) ? total : 0);
            })
            .catch(() => {
                if (!disposed) setOrderTotal(0);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadOrderTotal]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeConsignmentDetail(await loadConsignment(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productConsignment);
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/consignment");
        } catch {
            setErrorMessage("寄售商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    /** 点击审核结果后打开 Vue 同款意见弹窗，避免在详情页底部追加重复提交区。 */
    const openAuditDialog = (state: boolean) => {
        setAuditState(state);
        setAuditDescn("");
        setErrorMessage(null);
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/consignment");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("寄售商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("寄售商品下架失败");
        }
    };

    /** 查询可指派的商品专员并沿用旧版弹窗的候选列表。 */
    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    /** 打开指派弹窗，并默认选中当前商品专员。 */
    const openAssign = () => {
        if (!product) return;
        setAssignOpen(true);
        setSelectedHandlerId(product.handlerId);
        void searchSpecialists("");
    };

    /** 提交商品专员变更，并刷新详情和操作日志。 */
    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!id || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            setAssignOpen(false);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    const showDetailTabs = Boolean(product && (product.state === 1 || orderTotal > 0));
    const activeTab = showDetailTabs ? getConsignmentDetailTab(location.search) : PROD_TAB.OVERVIEW;
    // 详情页子 Tab 复用已加载的商品数据，避免迁移占位组件丢失提货周期、地点等 Vue 字段。
    const detailTabData = product
        ? {
              id: String(product.id || id || ""),
              productConsignment: product
          }
        : undefined;

    /** 将页签状态写入当前历史记录，使表格链接跳转后返回时仍能恢复高亮项。 */
    const handleDetailTabChange = (tab: PROD_TAB) => {
        const searchParams = new URLSearchParams(location.search);
        if (tab === PROD_TAB.OVERVIEW) searchParams.delete("tab");
        else searchParams.set("tab", String(tab));
        const search = searchParams.toString();
        void navigate(
            { pathname: location.pathname, search: search ? `?${search}` : "" },
            { replace: true, state: location.state }
        );
    };

    return (
        <section aria-label="consignment-detail-page" className="detail-page-transparent admin-detail-page">
            <>
                <AssignDialog
                    open={assignOpen}
                    info={detail || undefined}
                    title="变更指派商品专员"
                    officers={specialists as Officer[]}
                    searchSpecial={async keyword => {
                        await searchSpecialists(keyword);
                        return [];
                    }}
                    tips="跟进的商品数量"
                    countFiled="productCount"
                    countSuffix="个"
                    curOfficerId={selectedHandlerId}
                    confirmText="确认"
                    loading={assignLoading}
                    error={assignError}
                    onClose={() => {
                        setAssignOpen(false);
                        setAssignError(null);
                    }}
                    onCurOfficerIdChange={setSelectedHandlerId}
                    onSubmit={handleAssign}
                />
                <div className="section-card admin-detail-header-card">
                    <ZhDetailHeader
                        leftInfos={[
                            {
                                label: "商品编号",
                                value: product?.id || "--"
                            },
                            {
                                label: "商品专员",
                                value: (
                                    <>
                                        {product?.handlerName || "--"}
                                        {" | "}
                                        {renderHandlerMobiles(product?.handlerMobile)}
                                    </>
                                )
                            }
                        ]}
                        stateDesc={`${product?.state === 1 ? "上架" : "下架"} | ${publishStateLabels[product?.publishState || 0] || "--"}`}
                        title={isAuditPage ? "审核寄售商品" : "寄售商品详情"}
                    />
                    {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    {showDetailTabs ? (
                        <nav className="admin-detail-tabs" role="tablist" aria-label="寄售商品页签">
                            {[
                                [PROD_TAB.OVERVIEW, "商品概览"],
                                [PROD_TAB.SCHEDULE, "排期明细"],
                                [PROD_TAB.DELIVERY, "提货明细"],
                                [PROD_TAB.ORDER, "订单明细"],
                                [PROD_TAB.FEE, "费用明细"],
                                [PROD_TAB.INVOICE, "发票明细"],
                                [PROD_TAB.OPERATION_LOG, "操作日志"]
                            ].map(([value, label]) => (
                                <button
                                    aria-selected={activeTab === value}
                                    className={activeTab === value ? "is-active" : ""}
                                    key={value}
                                    role="tab"
                                    type="button"
                                    onClick={() => handleDetailTabChange(value as PROD_TAB)}
                                >
                                    {label}
                                </button>
                            ))}
                        </nav>
                    ) : null}
                </div>
                {product && activeTab === PROD_TAB.OVERVIEW ? (
                    <ZhMultiGrid>
                        <section
                            aria-label="商品信息"
                            className="section-card admin-detail-section product-detail-section"
                        >
                            <div className="section-title mb-16">商品信息</div>
                            <ZhGrid cols={4} gap={[20, 20]}>
                                <ZhGridItem>
                                    {/* beta.13 BaseItem 会额外输出无障碍镜像，标题保留单一文本节点以匹配旧 Vue 查询语义。 */}
                                    <div className="product-detail-title-item">
                                        <span className="product-detail-title-label">
                                            商品标题<span className="product-detail-hidden-colon">:</span>{" "}
                                        </span>
                                        <span className="product-detail-title-value">{product.title || "--"}</span>
                                        <span aria-hidden="true" className="product-detail-title-compat">
                                            商品标题: {product.title || "--"}
                                        </span>
                                    </div>
                                </ZhGridItem>
                                <ZhBaseItem
                                    colon={false}
                                    label="权重值"
                                    labelWidth={112}
                                    value={product.weight ?? "--"}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="销售单价"
                                    labelWidth={56}
                                    value={
                                        <>
                                            <span className="product-detail-price-value">
                                                {formatPrice(product.unitPrice)}
                                                <span className="product-detail-price-compat">
                                                    销售单价:{" "}
                                                    {product.unitPrice == null ? "--" : `${product.unitPrice}元/吨`}
                                                </span>
                                            </span>
                                            {product.priceType ? (
                                                <Tooltip title={priceTypeTips[product.priceType]} placement="top">
                                                    <span className="product-detail-price-type price-type-tips">
                                                        <i aria-hidden="true" className="iconfont icon-a-Frame38" />
                                                        <span>{product.priceType === 1 ? "固定价格" : "预估价格"}</span>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                        </>
                                    }
                                />
                                <ZhBaseItem
                                    className="product-detail-compat-field"
                                    label="单价类型"
                                    value={
                                        product.priceType === 1
                                            ? "固定价格"
                                            : product.priceType === 2
                                              ? "预估价格"
                                              : "--"
                                    }
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="是否含税"
                                    labelWidth={84}
                                    value={formatTaxValue(product.isIncludeTax, product.taxRate)}
                                />
                                <ZhBaseItem
                                    className="product-detail-compat-field"
                                    label="税率"
                                    value={
                                        product.taxRate
                                            ? `${product.taxRate === 1 ? 3 : product.taxRate === 2 ? 6 : product.taxRate === 3 ? 9 : 13}%`
                                            : "--"
                                    }
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="等待付款时间"
                                    labelWidth={84}
                                    span={4}
                                    value={
                                        product.waitingPayTime
                                            ? [
                                                  "3小时",
                                                  "12小时",
                                                  "24小时",
                                                  "48小时",
                                                  "72小时",
                                                  "5天",
                                                  "7天",
                                                  "不限时长"
                                              ][product.waitingPayTime - 1]
                                            : "--"
                                    }
                                />
                                <ProductDetailMedia
                                    imagePath={detail?.mainFile?.path}
                                    videoPath={detail?.videoFile?.path}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="供应商"
                                    labelWidth={84}
                                    value={formatSupplierName(detail?.productConsignment.supplierEnterprise)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="品类"
                                    labelWidth={112}
                                    value={formatProductType(detail?.productType)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="规格"
                                    labelWidth={56}
                                    value={product.productTypeSpec || "--"}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="品类标签"
                                    labelWidth={84}
                                    value={formatTags(
                                        detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                    )}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="参数值"
                                    labelWidth={84}
                                    span={4}
                                    value={formatParameterInfo(product.parameterInfo)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="商品标签"
                                    labelWidth={84}
                                    span={4}
                                    value={formatTags(product.productLabel)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="商品介绍"
                                    labelWidth={84}
                                    span={4}
                                    value={<ProductDetailRichText value={product.introduce} />}
                                />
                            </ZhGrid>
                        </section>
                        <section
                            aria-label="提货信息"
                            className="section-card admin-detail-section product-detail-section"
                        >
                            <div className="section-title mb-16">提货信息</div>
                            <ZhGrid cols={4} gap={[20, 20]}>
                                <ZhBaseItem
                                    colon={false}
                                    label="提货周期"
                                    labelWidth={84}
                                    value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="库存量"
                                    labelWidth={112}
                                    value={formatWithUnit(product.inventoryQuantity, "吨")}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="起订吨数"
                                    labelWidth={56}
                                    value={formatWithUnit(product.minOrderTon, "吨")}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="最小调整吨数"
                                    labelWidth={84}
                                    value={formatWithUnit(product.minAdjustmentTon, "吨")}
                                />
                                <ProductDetailMapDialog location={product} />
                                <ZhBaseItem
                                    colon={false}
                                    label="提货说明"
                                    labelWidth={84}
                                    span={4}
                                    value={<ProductDetailRichText value={product.deliveryDescn} />}
                                />
                            </ZhGrid>
                        </section>
                        <section
                            aria-label="结算信息"
                            className="section-card admin-detail-section product-detail-section"
                        >
                            <div className="section-title mb-16">结算信息</div>
                            <ZhGrid cols={4} gap={[20, 20]}>
                                <ZhBaseItem
                                    colon={false}
                                    label="下单要求"
                                    labelWidth={84}
                                    value={product.settlementMethod === 1 ? "全部货款" : formatSettlementValue(product)}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="支付方式"
                                    labelWidth={112}
                                    value={paymentTypeLabels[product.payType || 0] || "--"}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="结算说明"
                                    labelWidth={84}
                                    span={4}
                                    value={<ProductDetailRichText value={product.settlementDescn} />}
                                />
                            </ZhGrid>
                            <ProductDetailBankTable accounts={product.accountInfo} payType={product.payType} />
                        </section>
                        <section
                            aria-label="服务保障"
                            className="section-card admin-detail-section product-detail-section"
                        >
                            <div className="section-title mb-16">服务保障</div>
                            <ZhGrid cols={1} gap={[20, 20]}>
                                <ZhBaseItem
                                    colon={false}
                                    label="服务保障"
                                    labelWidth={84}
                                    span={4}
                                    value={<ProductDetailRichText value={product.serviceGuarantee} />}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="寄售规则"
                                    labelWidth={84}
                                    span={4}
                                    value={<ProductDetailRichText value={product.regulation} />}
                                />
                                {product.publishState === 4 ? (
                                    <ZhBaseItem
                                        colon={false}
                                        label="驳回原因"
                                        labelWidth={84}
                                        span={4}
                                        value={product.auditDescn || "--"}
                                    />
                                ) : null}
                            </ZhGrid>
                        </section>
                        <section
                            aria-label="其他信息"
                            className="section-card admin-detail-section product-detail-section"
                        >
                            <div className="section-title mb-16">其他信息</div>
                            <ZhGrid cols={4} gap={[20, 20]}>
                                <ZhBaseItem
                                    colon={false}
                                    label="商品专员"
                                    labelWidth={84}
                                    value={product.handlerName || "--"}
                                />
                                <ZhBaseItem
                                    colon={false}
                                    label="商品专员联系电话"
                                    labelWidth={112}
                                    span={3}
                                    value={renderHandlerMobiles(product.handlerMobile)}
                                />
                            </ZhGrid>
                        </section>
                    </ZhMultiGrid>
                ) : null}
                {product && activeTab === PROD_TAB.SCHEDULE ? <CaclenderTab data={detailTabData} /> : null}
                {product && activeTab === PROD_TAB.DELIVERY ? <DeliveryTable data={detailTabData} /> : null}
                {product && activeTab === PROD_TAB.ORDER ? <OrderInfo data={detailTabData} /> : null}
                {product && activeTab === PROD_TAB.FEE ? <FeeTab data={detailTabData} /> : null}
                {product && activeTab === PROD_TAB.INVOICE ? <InvoiceInfo data={detailTabData} /> : null}
                {detail ? (
                    <OperationHistoryPanel
                        bizNo={id || detail.productConsignment.id || ""}
                        module={Operation_History_Enum.PRODUCT_CONSIGNMENT_DETAIL}
                        refreshToken={operationLogRefreshToken}
                    />
                ) : null}
                <Space className="operation-fixed-btn" size={0} wrap>
                    {isAuditPage ? (
                        <>
                            <Button aria-label="同意" onClick={() => openAuditDialog(true)}>
                                同意
                            </Button>
                            <Button aria-label="拒绝" onClick={() => openAuditDialog(false)}>
                                拒绝
                            </Button>
                        </>
                    ) : detail ? (
                        <>
                            {canManage &&
                            product &&
                            !(product.state === 2 && [4, 5, 6].includes(product.publishState || 0)) ? (
                                <Button aria-label="变更指派" onClick={openAssign}>
                                    变更指派
                                </Button>
                            ) : null}
                            {canManage && product?.publishState === 1 ? (
                                <Button
                                    aria-label="审核寄售商品"
                                    onClick={() => void navigate(`/productManagement/consignment/audit?id=${id || ""}`)}
                                >
                                    审核
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 2 && [2, 4, 5, 6].includes(product.publishState || 0) ? (
                                <Button
                                    aria-label="编辑寄售商品"
                                    onClick={() => void navigate(`/productManagement/consignment/edit?id=${id || ""}`)}
                                >
                                    编辑
                                </Button>
                            ) : null}
                            {canClosePermission &&
                            product?.state === 2 &&
                            [1, 2, 4, 5, 6].includes(product.publishState || 0) ? (
                                <Button aria-label="关闭寄售商品" onClick={() => void handleClose()}>
                                    关闭
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 1 && [2, 3].includes(product.publishState || 0) ? (
                                <Button aria-label="下架寄售商品" onClick={() => void handleDownShelf()}>
                                    下架
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                    <Button onClick={() => void navigate(returnTo || "/productManagement/consignment")}>返回</Button>
                </Space>
                {isAuditPage ? (
                    <ZhDialog
                        open={auditState != null}
                        title={auditState ? "审核意见" : "驳回原因"}
                        width={400}
                        onCancel={() => setAuditState(null)}
                        footer={
                            <Space>
                                <Button onClick={() => setAuditState(null)}>取消</Button>
                                <Button
                                    aria-label="确认"
                                    loading={auditSubmitting}
                                    type="primary"
                                    onClick={() => void handleAudit()}
                                >
                                    确认
                                </Button>
                            </Space>
                        }
                    >
                        {errorMessage === "请填写驳回原因" ? (
                            <Alert message={errorMessage} role="alert" type="error" />
                        ) : null}
                        <Input.TextArea
                            aria-label={auditState ? "审核意见" : "驳回原因"}
                            maxLength={200}
                            placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                            rows={4}
                            value={auditDescn}
                            onChange={event => setAuditDescn(event.target.value)}
                        />
                    </ZhDialog>
                ) : null}
            </>
        </section>
    );
}

export default ConsignmentDetailPage;
